<template>
  <div class="pdf-viewer-wrapper">
    <!-- Overlay spinner -->
    <div v-if="loading" class="pdf-overlay-spinner">
      <div class="spinner"></div>
    </div>
    <div ref="map" style="height: 500px;"></div>
    <button v-if="showLayersControl" v-on:click="save()">Save</button>
  </div>
</template>

<script>
import moment from 'moment';
import axios from 'axios';
import levenshtein from 'js-levenshtein';

export default {
  name: "PdfLeafletViewer",
  props: {
    s3Url: { type: String },
    plan: { type: Object }
  },
  data() {
    return {
      map: null,
      L: null,
      clusterGroup: null,
      currentPage: 1,
      totalPages: 1,
      loading: true,
      snippetList: [],
      devices: {},
      pageMarkers: {},
      imageLayer: null,
      pdfControl: null,
      matches: [],
      ambigous: [],
      bad: [],
      nondefined: [],
      placements: [],
      heatmap: [],
      heatmapLayer: null,
      textRectsGroup: null,
      featuresList: [],
      showLayersControl: false,   // hidden feature flag
      layersCounts: {
        matched: 0,
        ambigous: 0,
        unknown: 0,
        heatmap: 0
      },
      layersControl: null
    };
  },
  methods: {
    save(){
      axios.put(`/customers/${this.$route.params.customer_id}/places/${this.$route.params.place_id}/documents/${this.plan.id}.json`, {document: {placements_attributes: this.placements}})
    },
    async loadLeafletDependencies() {
      if (!window.L) {
        await new Promise((resolve) => {
          const link = document.createElement("link");
          link.rel = "stylesheet";
          link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
          document.head.appendChild(link);

          const script = document.createElement("script");
          script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
          script.onload = resolve;
          document.body.appendChild(script);
        });
      }

      if (!window.L.MarkerClusterGroup) {
        await new Promise((resolve) => {
          const link = document.createElement("link");
          link.rel = "stylesheet";
          link.href =
            "https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.Default.css";
          document.head.appendChild(link);

          const script = document.createElement("script");
          script.src =
            "https://unpkg.com/leaflet.markercluster@1.5.3/dist/leaflet.markercluster.js";
          script.onload = resolve;
          document.body.appendChild(script);
        });
      }
      await new Promise((resolve) => {
          const script = document.createElement("script");

          script.src =
            "https://cdn.jsdelivr.net/npm/heatmap.js@2.0.5/build/heatmap.js";
          script.onload = resolve;
          document.body.appendChild(script);
        });
      await new Promise((resolve) => {
          const script = document.createElement("script");

          script.src =
            "https://cdn.jsdelivr.net/npm/leaflet-heatmap@1.0.0/leaflet-heatmap.min.js";
          script.onload = resolve;
          document.body.appendChild(script);
        });
      await new Promise((resolve) => {
          const script = document.createElement("script");

          script.src =
            "https://docs.opencv.org/4.x/opencv.js";
          script.onload = resolve;
          document.body.appendChild(script);
        });

    },
    clearPageState() {
      this.okGroup?.clearLayers();
      this.ambigousGroup?.clearLayers();
      this.badGroup?.clearLayers();
      this.textRectsGroup?.clearLayers();

      if (this.heatmapLayer) {
        this.map.removeLayer(this.heatmapLayer);
        this.heatmapLayer = null;
      }

      this.heatmap = [];
      this.matches = [];
      this.ambigous = [];
      this.bad = [];
      this.nondefined = [];
      this.placements = [];
    },
    doesFit(str1, str2, threshold = 0.2) {
        // Clean up the second string (remove "(X)" and trim spaces)
      const cleanedStr2 = str2.replace(/\(.*?\)\s*/, '').trim();

      // Check if one string is a substring of the other
      if (cleanedStr2.includes(str1) || str1.includes(cleanedStr2)) {
        return true;
      }

      // Calculate Levenshtein distance
      const distance = levenshtein(str1, cleanedStr2);

      // Normalize the distance
      const maxLength = Math.max(str1.length, cleanedStr2.length);
      const normalizedDistance = distance / maxLength;

      // Check if it fits within the threshold
      return normalizedDistance <= threshold;
    },
    createLayersControl() {
      const overlays = {
       [ `✅ Matched (${this.matches.length})`]: this.okGroup,
       [`⚠️ Ambiguous (${this.ambigous.length})`]: this.ambigousGroup,
       [`❌ Undefined (${this.bad.length})`]: this.badGroup,
       [` Set (${this.placements.length})`]: this.okGroup,
      };

      if (this.heatmapLayer) {
        overlays["🔥 Heatmap"] = this.heatmapLayer;
      }
      if (this.textRectsGroup) {
        overlays[`🔤 Text Rects (${this.featuresList.length})`] = this.textRectsGroup;
      }

      // Optional: allow toggling the PDF page itself
      const baseLayers = {
        "📄 PDF Page": this.imageLayer
      };

      this.layersControl = this.L.control.layers(
        baseLayers,
        overlays,
        { collapsed: false }
      );

      this.layersControl.addTo(this.map);
    },
    similarityScore(a, b) {
      if (!a || !b) return 1;

      const s1 = a.toUpperCase().trim();
      const s2 = b.toUpperCase().trim();

      const distance = levenshtein(s1, s2);
      return distance / Math.max(s1.length, s2.length);
    },
     
    findClosestFit(text) {
      let best = null;

      for (const cat of this.deviceCategories) {
        const score = this.similarityScore(text, cat.name);
        if (!best || score < best.score) {
          best = {
            name: cat.name,
            color: cat.color,
            score
          };
        }
      }

      return best;
    },

    async initMap() {
      this.L = window.L;
      this.map = this.L.map(this.$refs.map, {
        crs: this.L.CRS.Simple,
        minZoom: -4,
        maxZoom: 4,
        attributionControl: false,
      });

      this.ambigousGroup = this.L.markerClusterGroup();
      this.okGroup = this.L.markerClusterGroup();
      this.badGroup = this.L.markerClusterGroup();
      // this.map.addLayer(this.ambigousGroup);
      // this.map.addLayer(this.okGroup);
      // this.map.addLayer(this.badGroup);
    },

    async loadPage(withPoints = false) {
      if (!this.L) return;
      this.loading = true;

      if (this.imageLayer) {
        this.map.removeLayer(this.imageLayer);
        this.imageLayer = null;
      }
      axios.get(`/customers/${this.$route.params.customer_id}/places/${this.$route.params.place_id}/documents/${this.plan.id}.json`).then((response) => {
        this.placements = response.data.placements;
        for (var i = this.placements.length - 1; i >= 0; i--) {
          var item = this.placements[i]
          const marker = this.L.circleMarker([item.x, item.y], {
            radius: 20,
            fillColor: '#0f0',
            fillOpacity: 1.0,
            color: "#000",
            weight: 1,
            draggable: true
          });
        this.okGroup.addLayer(marker);
        }
        
      });


      const { createPdfLeafletLayer, findClosestImageMatches } = await import("./utils/pdfToLeaflet");
      const { imageLayer, bounds, markersList, featuresList } = await createPdfLeafletLayer(
        this.plan.attachment != undefined ? this.plan.attachment : this.s3Url,
        this.currentPage,
        5,
        withPoints,
        true
      );

      this.pageMarkers[this.currentPage] ||= [];
      for (var i = 0; i < this.deviceCategories.length; i++) {
        for (var j = 0; j < featuresList.length; j++) {
          const categoryName = this.deviceCategories[i].name.toUpperCase();
          const featureStr  = featuresList[j].str.toUpperCase(); 
          if (this.doesFit(categoryName, featureStr)) {
            featuresList[j].color = this.deviceCategories[i].color;

            // Create a Leaflet marker
            const marker = this.L.circleMarker([featuresList[j].pdfY, featuresList[j].pdfX], {
              radius: 20,
              fillColor: this.deviceCategories[i].color,
              color: featuresList[j].color || "#000",
              weight: 1,
              draggable: true,
              fillOpacity: 0.8,
            });
            
            // Bind a popup to the marker
            marker.bindPopup(`
              <div style="text-align:center;">
                <div>Category: <b>${this.deviceCategories[i].name}</b></div>
                <div>Feature: <b>${featuresList[j].str}</b></div>
              <img src="${featuresList[j].preview}" style="height:40px;margin-top:4px;" />
              <button id="saveButton">Zapisz</button>
              </div>
            `);



            // Add the marker to the cluster group
            // this.clusterGroup.addLayer(marker);

            // Add the marker to the pageMarkers array
            this.pageMarkers[this.currentPage].push({ marker, data: featuresList[j] });

            break;
          }
        }
      }
      if (!this.textRectsGroup) {
        this.textRectsGroup = this.L.layerGroup();
      } else {
        this.textRectsGroup.clearLayers();
      }

      this.imageLayer = imageLayer;
      this.imageLayer.addTo(this.map);
      this.map.fitBounds(bounds);

      this.snippetList = markersList;
       var that = this;
       var groups = _.groupBy(markersList, 'str');
      if (withPoints && this.snippetList) {
        console.log("Reloading");
        this.heatmap = []
        this.snippetList.forEach((item) => {
           var devs = "";
          // Assuming you have a Leaflet map instance `map`
          var color = "#999";
          if(_.isUndefined(that.devices[item.str.trim()]) || _.isUndefined(groups[item.str.trim()])){
            color = '#f00'
            this.bad.push(item.str);
          }else if(that.devices[item.str.trim()].length > 1 || groups[item.str.trim()] > 1){
            this.ambigous.push(item.str);

            color = '#ff0'
            const options = that.devices[item.str.trim()].map(
              (device) => `<option value="${device.id}">${device.number}</option>`
            ).join("");
            devs = "<select id='selectedDevice'>" + options + "</select>";


          }else{
            this.matches.push(item.str);
            color = '#0f0'
            var device = that.devices[item.str.trim()][0];
            if(that.properties[device.id] != undefined && that.properties[device.id][0].conditions != null){
              that.heatmap.push({
                lat: item.pdfY,
                lng: item.pdfX,
                count: that.properties[device.id] ? that.properties[device.id][0].conditions : 0
              });
            }
            
          }
          const marker = this.L.circleMarker([item.pdfY, item.pdfX], {
            radius: 20,
            fillColor: color,
            fillOpacity: 1.0,
            color: "#000",
            weight: 1,
            draggable: true
          });
          
          

        
          marker.on('click', function (e) {
            if(that.devices[item.str.trim()].length > 1 || groups[item.str.trim()] > 1){
            const options = that.devices[item.str.trim()].map(
              (device) => {
                if (_.find(that.placements, { device_id: device.id }) == undefined) {
                  return `<option value="${device.id}">${device.number}</option>`;
                }else {
                  return ''
                }
              }

            ).join("");
            devs = "<select id='selectedDevice'>" + options + "</select>";


          }
              const popupContent = `<div style="text-align:center;">
                <div>Number: <b>${item.str}</b></div>
                <div>${that.devices[item.str.trim()] == undefined ? "Unknown device" : that.devices[item.str.trim()][0].number}</div>
                <div>
                  ${
                    (that.devices[item.str.trim()]?.[0]?.id && that.properties[that.devices[item.str.trim()]?.[0]?.id]?.[0]?.conditions) 
                      ?? "-"
                  }
                </div>
                <img src="${item.preview}" style="width:40px;height:40px;border:1px solid #000;margin-top:4px;" />
                ${devs}
                <button id="saveButton">Zapisz</button>
              </div>`;

              // Create and open the popup dynamically
              L.popup()
                  .setLatLng(e.latlng) // Set the popup location to the marker's location
                  .setContent(popupContent) // Set the content of the popup
                  .openOn(that.map); // Open the popup on the map
               // Bind event to the "Zapisz" button after the popup is added to the DOM
          setTimeout(() => {
            const saveButton = document.getElementById('saveButton');
            const selectedDevice = document.getElementById('selectedDevice');
            console.log(saveButton);
            if (saveButton) {
              saveButton.addEventListener('click', function () {
                console.log({device_id: selectedDevice.value, x: marker.getLatLng().lat, y: marker.getLatLng().lng, page: that.currentPage}, item);
                if(_.find(that.placements, { device_id: selectedDevice.value }) == undefined){
                  var newPlacement = {
                    device_id: selectedDevice.value,
                    x: marker.getLatLng().lat,
                    y: marker.getLatLng().lng,
                    page: that.currentPage,
                    item: item
                  };
                  that.placements.push(newPlacement);
                  that.ambigousGroup.removeLayer(marker);
              }

                // Add your custom logic here
              });
            }
          }, 0);
          });

         
          if(color == '#0f0'){
            that.okGroup.addLayer(marker);
          }else if(color == '#ff0'){
            that.ambigousGroup.addLayer(marker);
          }else{
            that.badGroup.addLayer(marker);
          }
          this.pageMarkers[this.currentPage].push({ marker, data: item });

        });
        
      }

      this.drawHeatmap();
      this.loading = false;
      if (!this.textRectsGroup) {
        this.textRectsGroup = this.L.layerGroup();
      } else {
        this.textRectsGroup.clearLayers();
      }

      // Add rectangles for all features
      featuresList.forEach(f => {
        f.closest = this.findClosestFit(f.str);
        if (f.closest && f.closest.score < 0.4) {
         
        this.featuresList.push(f);
        const x = f.pdfX;
        const y = f.pdfY;
        const w = f.width || 30;   // fallback if width is missing
        const h = f.height || 10;  // fallback if height is missing
        // const topLeft = [yLeaflet - h/2, xCenter - w/2];
        // const bottomRight = [yLeaflet + h/2, xCenter + w/2];
        const rect = this.L.rectangle(
          [
            [y - h/2, x - w/2],
            [y + h/2, x + w/2]
          ],
          {
            color: '#007bff',
            weight: 1,
            fillOpacity: 0.15
          }
        );
        const searchedArea = f.preview.searchedArea;
        const boundinfox = f.preview.boundingBox;
        
        const redRect = this.L.rectangle(
          [
            [searchedArea.topLeft.y, searchedArea.topLeft.x],
            [searchedArea.bottomRight.y, searchedArea.bottomRight.x],
          ],
          {
            color: "red",
            weight: 1,
            fillOpacity: 0.15,
          }
        );
        let content = `
          <div style="font-size:12px; text-align:center;">
            <b>${f.str}</b><br/>
            <p>${
              f.closest
                ? `<span style="color:#555">
                    closest: <b>${f.closest.name}</b><br/>
                    score: ${(1 - f.closest.score).toFixed(2)}
                  </span>`
                : ''
            }
            </p>
            ${f.preview ? `<img src="${f.preview.preview}" style="margin-top:2px;"/>` : ''}
          </div>
        `;

        rect.bindTooltip(content, { sticky: true, direction: 'top' });
        this.textRectsGroup.addLayer(rect);
        this.textRectsGroup.addLayer(redRect);
      }
      });
      findClosestImageMatches(this.snippetList, this.featuresList).then((matches)=> {
        console.log(matches);
      });
    },
    drawHeatmap(){
      if (!this.heatmap.length) return;
      var cfg = {
        "radius": 300,
        "maxOpacity": .8,
        "scaleRadius": true,
        "useLocalExtrema": false,
        latField: 'lat',
        lngField: 'lng',
        valueField: 'count',
        gradient: {0.25: '#0f0', 0.5: '#ff0', 0.75: 'red', 1.0: '#8B0000'}
      };
      if(this.heatmapLayer) {
        this.map.removeLayer(this.heatmapLayer)
      }
      this.heatmapLayer = new HeatmapOverlay(cfg);

      if (this.showLayersControl) { this.map.addLayer(this.heatmapLayer); }
      this.heatmapLayer.setData({
        max: 4,
        data: this.heatmap
      });
      // heatmapLayer.setData({
      //   max: 5,
      //   data: [
      //     { lat: 100, lng: 100, count: 1 },
      //     { lat: 150, lng: 150, count: 3 },
      //     { lat: 200, lng: 200, count: 5 }
      //   ]
      // });
    },
    switchPage(pageNumber) {
      if (pageNumber === this.currentPage) return;

       this.clearPageState();
      this.currentPage = pageNumber;

      this.loadPage(true).then(() => {
        if (this.showLayersControl) {
          this.createLayersControl(); // refresh counts & layers
        }
        this.updatePdfControl();
      });
    },

    createPdfControl() {
      const L = this.L;
      const Control = L.Control.extend({
        options: { position: "topright" },
        onAdd: () => {
          const container = L.DomUtil.create(
            "div",
            "leaflet-pdf-horizontal-control"
          );
          L.DomEvent.disableClickPropagation(container);

          const btnContainer = L.DomUtil.create("div", "pdf-page-buttons", container);

          this.pdfControl = container;
          this.updatePdfControl();

          return container;
        },
      });

      this.map.addControl(new Control());
    },

    updatePdfControl() {
      if (!this.pdfControl) return;
      const btnContainer = this.pdfControl.querySelector(".pdf-page-buttons");
      btnContainer.innerHTML = "";

      for (let i = 1; i <= this.totalPages; i++) {
        const btn = this.L.DomUtil.create('a', '', btnContainer)
        btn.textContent = i;
        btn.title = `Go to page ${i}`
        btn.style.display = 'block'
        btn.style.padding = '6px 10px'
        btn.style.margin = '2px 0'
        btn.style.borderRadius = '4px'
        btn.style.textAlign = 'center'
        btn.style.cursor = 'pointer'
        btn.style.userSelect = 'none'
        btn.style.border = '1px solid #888'
        btn.style.color = i === this.currentPage ? 'white' : '#333'
        btn.style.backgroundColor = i === this.currentPage ? '#007bff' : 'transparent'
        btn.style.transition = 'background-color 0.3s, color 0.3s'
        btn.className = i === this.currentPage ? "active" : "";
        btn.onclick = () => this.switchPage(i);
      }
    },
    fetch_inspections: async function() {
      this.loaded_inspections = false
      var from = moment().subtract(1, 'years').startOf('day');
      var to = moment().endOf('day');
      var response = await axios.get('/customers/' + this.$route.params.customer_id + '/places/' + this.$route.params.place_id + '/inspections/' + moment(from).unix() + '/' + moment(to).unix() + '.json')
      this.inspections = response.data
      this.inspections.sort(function(a, b) {
          var va = new Date(a.date);
          var vb = new Date(b.date);
          return va - vb;
      });
      var response = await axios.get('/customers/' + this.$route.params.customer_id + '/places/' + this.$route.params.place_id + '/devices/properties/' + moment(from).unix() + '/' + moment(to).unix() + '.json')
      this.properties = _.groupBy(response.data, 'device_id');
      this.loaded_inspections = true

    },
  },
  
  async mounted() {
    window.addEventListener("keydown", (e) => {
      if (e.shiftKey && e.key === "L") {
        this.showLayersControl = !this.showLayersControl;
        if (this.showLayersControl) {
          this.createLayersControl();
        } else if (this.layersControl) {
          this.map.removeControl(this.layersControl);
        }
      }
    });
    await this.loadLeafletDependencies();
    await this.initMap();

    var response = await axios.get('/device_categories.json')
    this.deviceCategories = response.data;
    console.log(this.deviceCategories);
    for (var i = this.deviceCategories.length - 1; i >= 0; i--) {
      console.log(this.deviceCategories[i].name.toUpperCase())
    }
    await this.fetch_inspections();
    response = await axios.get('/customers/'+this.$route.params.customer_id+'/places/'+this.$route.params.place_id+'/devices.json');
    this.devices = _.groupBy(response.data, 'dev_num');
    for(var device of Object.keys(this.devices)){
      if(this.devices[device].length > 1){
        this.ambigous.push(device);
      }else{
        this.matches.push(device);
      }
    }
    
    const { getDocument } = await import("./utils/pdfToLeaflet");
    const pdf = await getDocument(this.plan.attachment ? this.plan.attachment : this.s3Url).promise;
    this.totalPages = pdf.numPages;

    this.createPdfControl();

    this.loadPage(true).then(() => {
      if (this.showLayersControl) {
        this.createLayersControl(); // refresh counts & layers
      }
      this.updatePdfControl();
    });
    
  }

};
</script>

<style scoped>
/deep/ .leaflet-zoom-hide {
  z-index: 2 !important;
}
.pdf-viewer-wrapper {
  position: relative;
  height: 500px;
}

/* Overlay spinner */
.pdf-overlay-spinner {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255,255,255,0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 5px solid rgba(0,0,0,0.2);
  border-top-color: #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Vertical page buttons */
.leaflet-pdf-vertical-control {
  background: rgba(255, 255, 255, 0.95);
  padding: 8px;
  border-radius: 4px;
  box-shadow: 0 0 6px rgba(0,0,0,0.3);
  max-height: 90%;
  overflow-y: auto;
  font-family: sans-serif;
}

.pdf-page-buttons button {
  display: block;
  width: 40px;
  margin: 2px 0;
  padding: 6px;
  border-radius: 4px;
  border: 1px solid #aaa;
  background: transparent;
  cursor: pointer;
}

.pdf-page-buttons button.active {
  background: #007bff;
  color: #fff;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/deep/ .pdf-page-buttons {
  display: flex;
  flex-wrap: wrap; /* Enable wrapping */
  flex-direction: column; /* Stack items vertically */
  max-height: 300px; /* Limit height to trigger wrapping */
  gap: 10px; /* Add spacing between items */
  overflow: auto; /* Allow scrolling if needed */
}

.pdf-page-buttons > a {
  flex: 1 1 auto; /* Allow buttons to grow/shrink */
  min-width: 100px; /* Optional: Set a minimum width */
}


</style>

